import { IInitialViewpoint, IViewpointComponent } from 'Data/interfaces/Activities/IViewpointData';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { visibleBimFiles } from 'Services/ViewpointRestoreService';
import { resolveUrnToFile } from './federationUrn';

export interface IViewpointModels {
  urns: string[];
  /**
   * `none`: o viewpoint não diz quais modelos usar — BCF de fora sem `Header`,
   * viewpoint antigo sem `BimFiles` nem componentes. Não se acende nada.
   */
  source: 'bimFiles' | 'components' | 'none';
  /** Lista de arquivos (ou motor) ainda não decidida: resolver de novo depois. */
  pending: boolean;
}

export interface IViewpointModelsContext {
  disciplines: IDisciplineViewerList[];
  /** Todas as disciplinas com arquivos e o motor da sessão decidido. */
  ready: boolean;
  activeUrns: string[];
  normalizeUrn: (raw?: string | null) => string | null;
  /** urn com que o arquivo entra na sessão; vazio = não abre nela. */
  urnForFile: (file: IFileData) => string;
}

const filesOf = (disciplines: IDisciplineViewerList[]): IFileData[] => (
  disciplines.flatMap((discipline) => discipline.FilesList ?? [])
);

/** Nome comparável entre revisões: sem extensão e sem o sufixo de revisão (R04, _R14, -R01). */
export const modelStem = (name?: string | null): string => (name ?? '')
  .toLowerCase()
  .replace(/\.[a-z0-9]+$/, '')
  .replace(/[._\- ]?r\d+$/, '');

/**
 * O arquivo cujo IFC tem esse `IfcProject`. O GUID é do projeto, não do arquivo:
 * as disciplinas de um prédio o compartilham, e o Revit repete o mesmo valor
 * entre projetos sem relação. O nome sem revisão desempata; sobrando modelos
 * diferentes, não se chuta. Entre revisões do mesmo modelo, vale a que já está
 * na cena, senão a mais recente.
 */
const fileForIfcProject = (
  ifcProject: string,
  filename: string,
  { disciplines, activeUrns, urnForFile }: IViewpointModelsContext,
): string | null => {
  const candidates = filesOf(disciplines)
    .filter((file) => file.FileInfo?.BucketFile?.IfcProjectGuid === ifcProject)
    .map((file) => ({ file, urn: urnForFile(file), stem: modelStem(file.NameWithoutRevision || file.Name) }))
    .filter(({ urn }) => !!urn);

  const byName = candidates.filter(({ stem }) => stem === modelStem(filename));
  const pool = byName.length > 0 ? byName : candidates;
  if (new Set(pool.map(({ stem }) => stem)).size > 1) { return null; }

  const active = pool.find(({ urn }) => activeUrns.includes(urn));
  if (active) { return active.urn; }

  const latest = pool.sort((a, b) => (
    (Date.parse(b.file.LastUpdate) || 0) - (Date.parse(a.file.LastUpdate) || 0)
  ))[0];
  return latest?.urn ?? null;
};

const componentsOf = (viewpoint: IInitialViewpoint): IViewpointComponent[] => [
  ...(viewpoint.Selection ?? []),
  ...(viewpoint.Visibility?.Exceptions ?? []),
  ...(viewpoint.Coloring ?? []).flatMap((coloring) => coloring.Components ?? []),
];

/**
 * Quais modelos o viewpoint pede, nesta ordem: urn dos `BimFiles` (ou o
 * `IfcProject` deles, quando a urn não está na obra — revisão nova ou BCF de
 * outro sistema), e, sem `BimFiles`, o `ModelUrn` dos componentes. O nome só
 * desempata dentro do mesmo `IfcProject`, e sem a revisão, que faz parte dele.
 */
export const resolveViewpointModels = (
  viewpoint: IInitialViewpoint,
  context: IViewpointModelsContext,
): IViewpointModels => {
  const { disciplines, ready, normalizeUrn } = context;
  const inObra = (raw?: string | null): string | null => {
    const urn = normalizeUrn(raw);
    return urn && resolveUrnToFile(urn, disciplines) ? urn : null;
  };

  const bimFiles = visibleBimFiles(viewpoint.BimFiles);
  const fromBimFiles = bimFiles
    .map((file) => inObra(file.Reference)
      ?? (file.IfcProject ? fileForIfcProject(file.IfcProject, file.Filename, context) : null))
    .filter((urn): urn is string => !!urn);

  const fromComponents = bimFiles.length > 0 ? [] : componentsOf(viewpoint)
    .map((component) => inObra(component.ComponentDetails?.ModelUrn))
    .filter((urn): urn is string => !!urn);

  const urns = Array.from(new Set(fromBimFiles.length > 0 ? fromBimFiles : fromComponents));
  if (urns.length > 0) {
    return { urns, source: fromBimFiles.length > 0 ? 'bimFiles' : 'components', pending: false };
  }

  // Antes da lista completa, "nada casou" pode ser só "ainda não chegou".
  return { urns: [], source: 'none', pending: !ready };
};
